// Auth + API client factory. Decoupled from any specific framework or store —
// pass in `getAccessToken` (sync read), `onTokenIssued` (refresh succeeded),
// and `onSignOut` (refresh failed → forget the user).
//
// Two concrete fetchers:
//   authFetch — services/auth (login flow, refresh, logout). Same-origin in
//               dev via Vite proxy; CORS in prod via single LB.
//   apiFetch  — services/api (everything else). Attaches Bearer access token.
//               On 401, tries ONE refresh through authFetch, retries the
//               original call once, and on second failure calls onSignOut.
//
// `inflightRefresh` deduplicates concurrent 401s so a burst of failing
// requests triggers exactly one rotation (otherwise reuse-detection would
// trip and revoke every session).

import { ApiError } from './errors.js';
import type { Me } from '@eidollon/contracts/auth';

export interface AuthClientConfig {
  /** services/auth base URL. Empty string = same-origin (Vite proxy in dev). */
  authBaseUrl: string;
  /** services/api base URL. "/api" same-origin via proxy in dev. */
  apiBaseUrl: string;
  /** Sync access-token reader. Returns null when unauthenticated. */
  getAccessToken: () => string | null;
  /** Refresh succeeded — store the new token + me. */
  onTokenIssued: (accessToken: string, me: Me) => void;
  /** Refresh failed or 2nd-attempt 401 — forget the user. */
  onSignOut: () => void;
}

export interface FetchOpts {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  /** Don't auto-refresh on 401. Used by the refresh call itself. */
  noRetry?: boolean;
}

export interface AuthClient {
  authFetch: <T = unknown>(path: string, opts?: FetchOpts) => Promise<T>;
  apiFetch: <T = unknown>(path: string, opts?: FetchOpts) => Promise<T>;
  /** Boot-time probe: try refresh; on success update token + me. */
  bootProbe: () => Promise<boolean>;
}

export function createAuthClient(cfg: AuthClientConfig): AuthClient {
  let inflightRefresh: Promise<boolean> | null = null;

  async function rawFetch(base: string, path: string, opts: FetchOpts): Promise<Response> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...(opts.headers ?? {}),
    };
    const init: RequestInit = {
      method: opts.method ?? 'GET',
      headers,
      credentials: 'include', // send refresh cookie
    };
    if (opts.body != null) {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(opts.body);
    }
    return fetch(base + path, init);
  }

  async function parseError(res: Response): Promise<never> {
    type ErrBody = {
      error?: string;
      request_id?: string;
      retry_after_seconds?: number;
      detail?: string;
    };
    let body: ErrBody | null = null;
    try {
      body = (await res.json()) as ErrBody;
    } catch {
      /* non-JSON 4xx */
    }
    throw new ApiError(
      body?.error ?? 'internal_error',
      res.status,
      body?.request_id,
      body?.retry_after_seconds,
      body?.detail,
    );
  }

  async function authFetch<T = unknown>(path: string, opts: FetchOpts = {}): Promise<T> {
    const res = await rawFetch(cfg.authBaseUrl, path, opts);
    if (!res.ok) await parseError(res);
    return res.json() as Promise<T>;
  }

  // Transient vs. definitive failure matters here: a network blip, a tunnel
  // reconnect, or a 5xx does NOT mean "no session" — the refresh cookie may
  // still be perfectly valid. Only a 401 from the server (session actually
  // expired/revoked/reused) is a definitive verdict. Treating every failure
  // the same way used to sign the user out on any transient hiccup — which
  // shows up constantly behind a tunnel/proxy even though it never happens
  // on stable localhost. Retry transient failures a couple of times with a
  // short backoff before giving up.
  const TRANSIENT_RETRY_DELAYS_MS = [300, 900];

  function isDefinitiveAuthFailure(err: unknown): boolean {
    return err instanceof ApiError && err.status === 401;
  }

  async function attemptRefreshOnce(): Promise<boolean> {
    const data = await authFetch<{
      access_token: string;
      expires_in: number;
      me: Me;
    }>('/v1/auth/refresh', { method: 'POST', noRetry: true });
    cfg.onTokenIssued(data.access_token, data.me);
    return true;
  }

  async function tryRefresh(): Promise<boolean> {
    if (inflightRefresh) return inflightRefresh;
    inflightRefresh = (async () => {
      try {
        for (const delay of [0, ...TRANSIENT_RETRY_DELAYS_MS]) {
          if (delay > 0) await new Promise((r) => setTimeout(r, delay));
          try {
            return await attemptRefreshOnce();
          } catch (err) {
            if (isDefinitiveAuthFailure(err)) return false;
            // Transient (network error, timeout, 5xx) — fall through to retry.
          }
        }
        // Exhausted retries on transient failures — still not a confirmed
        // "no session", but we can't stall the caller forever. Report
        // failure without having proven the session invalid; the next
        // natural refresh attempt (e.g. on the next request) gets another
        // clean shot.
        return false;
      } finally {
        inflightRefresh = null;
      }
    })();
    return inflightRefresh;
  }

  async function apiFetch<T = unknown>(path: string, opts: FetchOpts = {}): Promise<T> {
    const headers: Record<string, string> = { ...(opts.headers ?? {}) };
    const access = cfg.getAccessToken();
    if (access) headers['Authorization'] = `Bearer ${access}`;
    const res = await rawFetch(cfg.apiBaseUrl, path, { ...opts, headers });

    if (res.status === 401 && !opts.noRetry) {
      const refreshed = await tryRefresh();
      if (refreshed) return apiFetch<T>(path, { ...opts, noRetry: true });
      cfg.onSignOut();
      await parseError(res);
    }

    if (!res.ok) await parseError(res);
    return res.json() as Promise<T>;
  }

  return { authFetch, apiFetch, bootProbe: tryRefresh };
}
